import React, { useEffect, useMemo, useState } from 'react';
import {
  PlusIcon,
  FileTextIcon,
  SendIcon,
  EyeIcon,
  XIcon,
  RefreshCwIcon,
  ArrowLeftIcon,
  PencilIcon,
  BellIcon,
  RepeatIcon,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Toast } from '../Toast';
import {
  useClientPlans,
  useInvalidatePlans,
  PlanRecord,
} from '../../hooks/usePlans';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../lib/api';
import { useMutation } from '@tanstack/react-query';
import { usePlanReminders, useInvalidateReminders } from '../../hooks/useReminders';
import { ReminderFormModal, ReminderFormData } from '../reminders/ReminderFormModal';
import { RecurringReminderForm, RecurringReminderFormData } from '../RecurringReminderForm';
import { useCreateRecurringTemplate } from '../../hooks/useRecurringTemplateMutations';

interface PlansTabProps {
  clientId: string;
  planIdFromUrl?: string | null;
}

type PlanModalState = {
  title: string;
  description: string;
  startDate: string; // YYYY-MM-DD format
  shareViaWhatsApp: boolean;
  file: File | null;
};

const initialModalState: PlanModalState = {
  title: '',
  description: '',
  startDate: new Date().toISOString().split('T')[0], // Today as default
  shareViaWhatsApp: false,
  file: null,
};

function formatDate(date?: string | null) {
  if (!date) return '—';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function calculateDuration(startDate: string, endDate?: string | null): number {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

export function PlansTab({ clientId, planIdFromUrl }: PlansTabProps) {
  const { session } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [modalState, setModalState] = useState<PlanModalState>(
    initialModalState,
  );
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PlanRecord | null>(null);
  const [editingPlan, setEditingPlan] = useState<PlanRecord | null>(null);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showRecurringReminderModal, setShowRecurringReminderModal] = useState(false);
  const [reminderType, setReminderType] = useState<'one-time' | 'recurring'>('one-time');
  const [reminderPlanContext, setReminderPlanContext] = useState<{
    planId: string;
    planTitle: string;
  } | null>(null);
  const invalidatePlans = useInvalidatePlans(clientId);
  const invalidateReminders = useInvalidateReminders(clientId);
  const createRecurringTemplateMutation = useCreateRecurringTemplate(clientId);

  const plansQuery = useClientPlans(clientId);
  const plans = plansQuery.data ?? [];

  // Auto-open plan viewer when planIdFromUrl is provided
  // Close plan viewer when planId is removed from URL
  useEffect(() => {
    if (planIdFromUrl && plans.length > 0) {
      const planToOpen = plans.find(p => p.id === planIdFromUrl);
      if (planToOpen) {
        setSelectedPlan(planToOpen);
      }
    } else if (!planIdFromUrl && selectedPlan) {
      // Close modal when planId is removed from URL
      setSelectedPlan(null);
    }
  }, [planIdFromUrl, plans]);

  const currentPlan = useMemo(
    () => plans.find((plan) => plan.is_current) ?? null,
    [plans],
  );

  const pastPlans = useMemo(
    () => plans.filter((plan) => !plan.is_current),
    [plans],
  );

  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    const mobileCheck = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      navigator.userAgent,
    );
    setIsMobileDevice(mobileCheck);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (selectedPlan) {
      const { body } = document;
      const previousOverflow = body.style.overflow;
      body.style.overflow = 'hidden';
      return () => {
        body.style.overflow = previousOverflow;
      };
    }
  }, [selectedPlan]);

  const closeModal = () => {
    setShowPlanModal(false);
    setEditingPlan(null);
    setModalState(initialModalState);
    setError(null);
  };

  const openCreatePlanModal = () => {
    setEditingPlan(null);
    setModalState(initialModalState);
    setShowPlanModal(true);
  };

  const openEditPlanModal = (plan: PlanRecord) => {
    setEditingPlan(plan);
    setModalState({
      title: plan.title,
      description: plan.description ?? '',
      startDate: plan.start_date,
      shareViaWhatsApp: false,
      file: null,
    });
    setShowPlanModal(true);
  };

  const sendPlanMutation = useMutation({
    mutationFn: async (plan: PlanRecord) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }

      return apiRequest(
        `/api/clients/${clientId}/plans/${plan.id}/send`,
        {
          method: 'POST',
        },
        session.access_token,
      );
    },
    onSuccess: () => {
      setToastMessage('Plan sent via WhatsApp');
      invalidatePlans();
    },
    onError: (err) => {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send plan via WhatsApp';
      setToastMessage(errorMessage);
    },
  });

  const handleResend = (plan: PlanRecord) => {
    sendPlanMutation.mutate(plan);
  };

  const createPlanMutation = useMutation({
    mutationFn: async () => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      if (!modalState.title.trim()) {
        throw new Error('Plan name is required');
      }

      const formData = new FormData();
      formData.append('title', modalState.title.trim());
      if (modalState.description.trim()) {
        formData.append('description', modalState.description.trim());
      }
      formData.append('start_date', modalState.startDate);
      if (modalState.shareViaWhatsApp) {
        formData.append('share_via_whatsapp', 'true');
      }
      formData.append('is_current', 'true');
      if (modalState.file) {
        formData.append('file', modalState.file);
      }

      return apiRequest(
        `/api/clients/${clientId}/plans`,
        {
          method: 'POST',
          body: formData,
        },
        session.access_token,
      );
    },
    onSuccess: () => {
      setToastMessage('Plan saved');
      closeModal();
      invalidatePlans();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to save plan');
    },
  });

  const markCurrentMutation = useMutation({
    mutationFn: async (planId: string) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      return apiRequest(
        `/api/clients/${clientId}/plans/${planId}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ is_current: true }),
        },
        session.access_token,
      );
    },
    onSuccess: () => {
      invalidatePlans();
    },
  });

  const updatePlanMutation = useMutation({
    mutationFn: async () => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      if (!editingPlan) {
        throw new Error('Select a plan to edit');
      }
      if (!modalState.title.trim()) {
        throw new Error('Plan name is required');
      }

      const payload: Record<string, unknown> = {
        title: modalState.title.trim(),
        start_date: modalState.startDate,
      };
      if (modalState.description.trim()) {
        payload.description = modalState.description.trim();
      } else {
        payload.description = null;
      }

      return apiRequest(
        `/api/clients/${clientId}/plans/${editingPlan.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
        },
        session.access_token,
      );
    },
    onSuccess: () => {
      setToastMessage('Plan updated');
      closeModal();
      invalidatePlans();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to update plan');
    },
  });

  const handlePlanSubmit = () => {
    if (editingPlan) {
      updatePlanMutation.mutate();
    } else {
      createPlanMutation.mutate();
    }
  };

  const isSavingPlan = editingPlan
    ? updatePlanMutation.isPending
    : createPlanMutation.isPending;

  const handleViewPlan = (plan: PlanRecord) => {
    if (!plan.download_url && !plan.description) {
      setToastMessage('Nothing to show for this plan yet.');
      return;
    }
    setSelectedPlan(plan);
  };

  const handleClosePlanViewer = () => {
    setSelectedPlan(null);

    // Remove planId from URL while keeping other params
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('planId');
    setSearchParams(newParams, { replace: true });
  };

  const handleFileAction = (plan: PlanRecord) => {
    if (!plan.download_url) return;

    if (typeof window === 'undefined') return;

    if (isMobileDevice && typeof document !== 'undefined') {
      const link = document.createElement('a');
      link.href = plan.download_url;
      link.download = plan.file_name || 'plan';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    window.open(plan.download_url, '_blank', 'noopener');
  };

  const handleOpenReminderModal = (plan: PlanRecord, type: 'one-time' | 'recurring' = 'one-time') => {
    setReminderPlanContext({ planId: plan.id, planTitle: plan.title });
    setReminderType(type);
    if (type === 'recurring') {
      setShowRecurringReminderModal(true);
    } else {
      setShowReminderModal(true);
    }
  };

  const handleCloseReminderModal = () => {
    setShowReminderModal(false);
    setShowRecurringReminderModal(false);
    setReminderPlanContext(null);
  };

  const createReminderMutation = useMutation({
    mutationFn: async (formData: ReminderFormData) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      if (!formData.title.trim()) {
        throw new Error('Title is required');
      }
      if (!formData.date || !formData.time) {
        throw new Error('Date and time are required');
      }

      const localDate = new Date(`${formData.date}T${formData.time}`);
      const isoDateTime = localDate.toISOString();

      const payload = {
        title: formData.title.trim(),
        description: formData.description?.trim() || null,
        due_at: isoDateTime,
        plan_id: formData.planId || null,
      };

      return apiRequest(
        `/api/clients/${clientId}/reminders`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
        session.access_token,
      );
    },
    onSuccess: () => {
      setToastMessage('Reminder created');
      handleCloseReminderModal();
      invalidateReminders();
    },
    onError: (err) => {
      setToastMessage(err instanceof Error ? err.message : 'Failed to create reminder');
    },
  });

  const handleRecurringReminderSubmit = async (formData: RecurringReminderFormData) => {
    const payload = {
      ...formData,
      plan_id: reminderPlanContext?.planId || null,
    };
    await createRecurringTemplateMutation.mutateAsync(payload);
    setToastMessage('Recurring reminder created');
    handleCloseReminderModal();
  };

  const renderPlanViewer = () => {
    if (!selectedPlan) return null;

    const startedOn = formatDate(selectedPlan.start_date);
    const sharedOn = formatDate(selectedPlan.shared_at);

    return <PlanViewerContent plan={selectedPlan} startedOn={startedOn} sharedOn={sharedOn} />;
  };

  const PlanViewerContent = ({ plan, startedOn, sharedOn }: { plan: PlanRecord; startedOn: string; sharedOn: string }) => {
    const remindersQuery = usePlanReminders(clientId, plan.id);
    const reminders = remindersQuery.data ?? [];
    const pendingReminders = reminders.filter(r => r.status === 'pending');

    return (
      <div className="fixed inset-0 z-50 bg-white">
        <div className="flex h-full flex-col bg-[#F5F5F5]">
          <div className="flex items-center gap-3 border-b border-gray-100 bg-white px-4 py-3">
            <button
              onClick={handleClosePlanViewer}
              className="rounded-full border border-gray-200 p-2 text-gray-700"
              aria-label="Back to client"
            >
              <ArrowLeftIcon className="h-4 w-4" />
            </button>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-gray-500">
                Plan preview
              </p>
              <h2 className="truncate text-base font-semibold text-[#212121]">
                {plan.title}
              </h2>
            </div>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto px-4 py-6">
            <section className="rounded-2xl bg-white p-4 grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500">Started on</p>
                <p className="text-sm font-medium text-[#212121]">{startedOn}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Shared on</p>
                <p className="text-sm font-medium text-[#212121]">{sharedOn || 'Not shared yet'}</p>
              </div>
            </section>

            {pendingReminders.length > 0 && (
              <section className="rounded-2xl bg-white p-5">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="rounded-full bg-[#C9A870] bg-opacity-10 p-2 text-[#C9A870]">
                      <BellIcon className="h-4 w-4" />
                    </div>
                    <p className="text-sm font-semibold text-[#212121]">Upcoming Reminders</p>
                  </div>
                  <button
                    onClick={() => handleOpenReminderModal(plan)}
                    className="text-xs text-[#5B8E87] font-medium hover:underline"
                  >
                    Add reminder
                  </button>
                </div>
                <div className="space-y-2">
                  {pendingReminders.map((reminder) => (
                    <div key={reminder.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <BellIcon className="w-4 h-4 text-[#C9A870] mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#212121]">{reminder.title}</p>
                        <p className="text-xs text-gray-600 mt-0.5">
                          {formatDate(reminder.due_at)} at {new Date(reminder.due_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {plan.description && (
              <section className="rounded-2xl bg-white p-5">
                <div className="mb-3 flex items-center gap-2">
                  <div className="rounded-full bg-[#5B8E87] bg-opacity-10 p-2 text-[#5B8E87]">
                    <FileTextIcon className="h-4 w-4" />
                  </div>
                  <p className="text-sm font-semibold text-[#212121]">Plan notes</p>
                </div>
                <p className="whitespace-pre-line text-sm leading-relaxed text-gray-700">
                  {selectedPlan.description}
                </p>
              </section>
            )}

            {plan.download_url && (
              <section className="rounded-2xl bg-white p-5">
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-sm font-semibold text-[#212121]">Attachment</p>
                  <span className="text-xs text-gray-500">
                    {isMobileDevice ? 'Download to device' : 'Opens in new tab'}
                  </span>
                </div>
                <button
                  onClick={() => handleFileAction(plan)}
                  className="flex w-full items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-4 text-left transition hover:border-[#5B8E87]/40 hover:bg-white"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-sm">
                    <FileTextIcon className="h-5 w-5 text-[#5B8E87]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-semibold text-[#212121]">
                      {plan.file_name || 'Plan file'}
                    </p>
                    <p className="text-xs text-gray-500">
                      Tap to {isMobileDevice ? 'download' : 'open the file'}
                    </p>
                  </div>
                </button>
              </section>
            )}

            {!plan.description && !plan.download_url && pendingReminders.length === 0 && (
              <section className="rounded-2xl bg-white p-5 text-center text-sm text-gray-600">
                Plan content will appear here once added.
              </section>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCurrentPlan = () => {
    if (plansQuery.isLoading) {
      return (
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 text-center">
          <p className="text-sm text-gray-500">Loading plans...</p>
        </div>
      );
    }

    if (!currentPlan) {
      return (
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-dashed border-gray-200 text-center">
          <h3 className="text-lg font-semibold text-[#212121] mb-2">
            No active plan
          </h3>
          <p className="text-gray-600 mb-4">
            Add a plan and mark it as active to highlight it here.
          </p>
          <button
            onClick={openCreatePlanModal}
            className="bg-[#5B8E87] text-white px-4 py-2 rounded-lg font-medium"
          >
            Add Plan
          </button>
        </div>
      );
    }

    return <CurrentPlanCard plan={currentPlan} />;
  };

  const CurrentPlanCard = ({ plan }: { plan: PlanRecord }) => {
    const remindersQuery = usePlanReminders(clientId, plan.id);
    const reminders = remindersQuery.data ?? [];
    const pendingReminders = reminders.filter(r => r.status === 'pending');
    const nextReminder = pendingReminders.length > 0 ? pendingReminders[0] : null;

    return (
      <div className="bg-gradient-to-r from-[#5B8E87] to-[#4F7A74] rounded-2xl shadow-sm p-5 text-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-base">Current Plan</h3>
            {pendingReminders.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#C9A870] bg-opacity-90 rounded-full text-xs font-medium">
                <BellIcon className="w-3 h-3" />
                {pendingReminders.length}
              </span>
            )}
          </div>
          <span className="text-xs px-2 py-1 rounded-full bg-white bg-opacity-20">
            Active
          </span>
        </div>
        <h2 className="text-xl font-bold mb-2">{plan.title}</h2>
        <p className="text-sm text-white text-opacity-80 mb-4">
          Started {formatDate(plan.start_date)} • Active for {calculateDuration(plan.start_date)} days
        </p>
        {nextReminder && (
          <div className="mb-4 bg-white bg-opacity-10 rounded-lg p-3">
            <p className="text-xs text-white text-opacity-70 mb-1">Next reminder:</p>
            <p className="text-sm font-medium">{nextReminder.title}</p>
            <p className="text-xs text-white text-opacity-70">
              {formatDate(nextReminder.due_at)} at {new Date(nextReminder.due_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 text-sm mb-4">
          <div>
            <p className="text-white text-opacity-80 mb-1">File</p>
            <p className="font-semibold">
              {plan.file_name || 'Not provided'}
            </p>
          </div>
          <div>
            <p className="text-white text-opacity-80 mb-1">Shared</p>
            <p className="font-semibold">
              {formatDate(plan.shared_at) || 'Not shared yet'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            disabled={!plan.download_url && !plan.description}
            onClick={() => handleViewPlan(plan)}
            className={`flex-1 px-2 py-2 rounded-lg font-medium text-sm flex items-center justify-center gap-1 ${
              plan.download_url || plan.description
                ? 'bg-white bg-opacity-20 hover:bg-opacity-30'
                : 'bg-white bg-opacity-10 cursor-not-allowed text-white text-opacity-60'
            }`}
          >
            <EyeIcon className="w-4 h-4 flex-shrink-0" />
            <span className="hidden sm:inline">View</span>
          </button>
          <button
            onClick={() => openEditPlanModal(plan)}
            className="flex-1 bg-white bg-opacity-20 text-white px-2 py-2 rounded-lg font-medium text-sm flex items-center justify-center gap-1 hover:bg-opacity-30 transition-colors"
          >
            <PencilIcon className="w-4 h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Edit</span>
          </button>
          <button
            onClick={() => handleResend(plan)}
            disabled={sendPlanMutation.isPending}
            className={`flex-1 bg-white bg-opacity-20 text-white px-2 py-2 rounded-lg font-medium text-sm flex items-center justify-center gap-1 transition-colors ${
              sendPlanMutation.isPending
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-opacity-30'
            }`}
          >
            <SendIcon className="w-4 h-4 flex-shrink-0" />
            <span className="hidden sm:inline">{sendPlanMutation.isPending ? 'Sending...' : 'Send'}</span>
          </button>
          <button
            onClick={() => handleOpenReminderModal(plan)}
            className="flex-1 bg-[#C9A870] bg-opacity-90 text-white px-2 py-2 rounded-lg font-medium text-sm flex items-center justify-center gap-1 hover:bg-opacity-100 transition-colors"
          >
            <BellIcon className="w-4 h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Reminder</span>
          </button>
        </div>
      </div>
    );
  };

  const PastPlanCard = ({ plan }: { plan: PlanRecord }) => {
    const remindersQuery = usePlanReminders(clientId, plan.id);
    const reminders = remindersQuery.data ?? [];
    const pendingReminders = reminders.filter(r => r.status === 'pending');

    return (
      <div
        key={plan.id}
        className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100"
      >
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h4 className="font-semibold text-sm text-[#212121]">
                {plan.title}
              </h4>
              {pendingReminders.length > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#C9A870] bg-opacity-10 rounded-full text-xs font-medium text-[#C9A870]">
                  <BellIcon className="w-3 h-3" />
                  {pendingReminders.length}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-600">
              {plan.end_date
                ? `${formatDate(plan.start_date)} - ${formatDate(plan.end_date)} (${calculateDuration(plan.start_date, plan.end_date)} days)`
                : `Started ${formatDate(plan.start_date)}`
              }
            </p>
            {plan.activation_count > 1 && (
              <p className="text-xs text-[#5B8E87] mt-1">
                Used {plan.activation_count} times • Last: {formatDate(plan.last_activated_at)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap sm:flex-nowrap">
            <button
              disabled={!plan.download_url && !plan.description}
              onClick={() => handleViewPlan(plan)}
              className={`p-2.5 rounded-lg touch-manipulation ${
                plan.download_url || plan.description
                  ? 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                  : 'bg-gray-100 text-gray-300 cursor-not-allowed'
              }`}
              aria-label="View plan"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleOpenReminderModal(plan)}
              className="p-2.5 bg-[#C9A870] bg-opacity-10 rounded-lg hover:bg-opacity-20 transition-colors text-[#C9A870] touch-manipulation"
              aria-label="Set reminder"
            >
              <BellIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleResend(plan)}
              disabled={sendPlanMutation.isPending}
              className={`p-2.5 bg-[#5B8E87] bg-opacity-10 rounded-lg transition-colors text-[#5B8E87] touch-manipulation ${
                sendPlanMutation.isPending
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-opacity-20'
              }`}
              aria-label="Send plan"
            >
              <SendIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => openEditPlanModal(plan)}
              className="p-2.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-gray-600 touch-manipulation"
              aria-label="Edit plan"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => markCurrentMutation.mutate(plan.id)}
              className="p-2.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-gray-600 touch-manipulation"
              aria-label="Mark as current"
            >
              <RefreshCwIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2 py-1 bg-gray-100 rounded-full text-gray-600">
            {plan.file_name || 'No file'}
          </span>
          {plan.shared_at && (
            <span className="px-2 py-1 bg-[#5B8E87] bg-opacity-10 rounded-full text-[#5B8E87]">
              Shared
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderPastPlans = () => {
    if (!pastPlans.length) {
      return (
        <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 text-center text-sm text-gray-500">
          No past plans yet.
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {pastPlans.map((plan) => (
          <PastPlanCard key={plan.id} plan={plan} />
        ))}
      </div>
    );
  };

  return (
    <div className="p-4 space-y-4">
      {renderCurrentPlan()}
      <div>
        <h3 className="font-semibold text-base text-[#212121] mb-3">
          Past Plans
        </h3>
        {renderPastPlans()}
      </div>

      <button
        onClick={openCreatePlanModal}
        className="fixed bottom-24 right-6 bg-[#C9A870] text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-[#B89660] transition-all active:scale-95 z-20"
      >
        <PlusIcon className="w-6 h-6" />
      </button>

      {showPlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#212121]">
                {editingPlan ? 'Edit Plan' : 'Add New Plan'}
              </h2>
              <button onClick={closeModal} className="text-gray-500">
                <XIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-2 rounded-xl">
                  {error}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[#212121] mb-2">
                  Plan Name
                </label>
                <input
                  type="text"
                  value={modalState.title}
                  onChange={(e) =>
                    setModalState((prev) => ({ ...prev, title: e.target.value }))
                  }
                  placeholder="e.g., Weight Loss Plan"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#212121] mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={modalState.startDate}
                  onChange={(e) =>
                    setModalState((prev) => ({ ...prev, startDate: e.target.value }))
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#212121] mb-2">
                  Description (optional)
                </label>
                <textarea
                  value={modalState.description}
                  onChange={(e) =>
                    setModalState((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all resize-none"
                />
              </div>
              {!editingPlan ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-[#212121] mb-2">
                      Upload File
                    </label>
                    <label className="w-full px-4 py-8 rounded-xl border-2 border-dashed border-gray-300 hover:border-[#5B8E87] transition-colors flex flex-col items-center justify-center gap-2 cursor-pointer">
                      <FileTextIcon className="w-8 h-8 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {modalState.file ? modalState.file.name : 'Click to upload'}
                      </span>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0] ?? null;
                          setModalState((prev) => ({ ...prev, file }));
                        }}
                      />
                    </label>
                  </div>
                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer">
                    <input
                      type="checkbox"
                      checked={modalState.shareViaWhatsApp}
                      onChange={(e) =>
                        setModalState((prev) => ({
                          ...prev,
                          shareViaWhatsApp: e.target.checked,
                        }))
                      }
                      className="w-5 h-5 rounded border-gray-300 text-[#5B8E87] focus:ring-[#5B8E87]"
                    />
                    <span className="text-sm text-[#212121] font-medium">
                      Share via WhatsApp
                    </span>
                  </label>
                </>
              ) : (
                <div className="text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
                  Attachments stay unchanged. To upload a new file, create a fresh plan.
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={closeModal}
                  className="flex-1 bg-gray-100 text-gray-600 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePlanSubmit}
                  disabled={isSavingPlan}
                  className={`flex-1 font-semibold py-3 rounded-xl transition-colors ${
                    isSavingPlan
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-[#5B8E87] text-white hover:bg-[#4F7A74]'
                  }`}
                >
                  {isSavingPlan
                    ? 'Saving...'
                    : editingPlan
                      ? 'Save Changes'
                      : 'Save Plan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {renderPlanViewer()}
      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}

      {(showReminderModal || showRecurringReminderModal) && reminderPlanContext && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-hidden animate-scale-in flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-[#212121]">Add Reminder</h2>
                <button
                  onClick={handleCloseReminderModal}
                  className="text-gray-500"
                >
                  <XIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Tabs for One-time vs Recurring */}
              <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => {
                    setReminderType('one-time');
                    setShowReminderModal(true);
                    setShowRecurringReminderModal(false);
                  }}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    reminderType === 'one-time'
                      ? 'bg-white text-[#212121] shadow-sm'
                      : 'text-gray-600 hover:text-[#212121]'
                  }`}
                >
                  One-time
                </button>
                <button
                  onClick={() => {
                    setReminderType('recurring');
                    setShowReminderModal(false);
                    setShowRecurringReminderModal(true);
                  }}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1 ${
                    reminderType === 'recurring'
                      ? 'bg-white text-[#212121] shadow-sm'
                      : 'text-gray-600 hover:text-[#212121]'
                  }`}
                >
                  <RepeatIcon className="w-3.5 h-3.5" />
                  Recurring
                </button>
              </div>

              {/* Plan context badge */}
              <div className="mt-3 bg-[#5B8E87] bg-opacity-10 border border-[#5B8E87] border-opacity-20 rounded-xl px-3 py-2">
                <p className="text-xs text-gray-600 mb-1">Linked to plan:</p>
                <p className="text-sm font-medium text-[#5B8E87]">{reminderPlanContext.planTitle}</p>
              </div>
            </div>

            {/* Form content - scrollable */}
            <div className="flex-1 overflow-y-auto">
              {reminderType === 'one-time' && showReminderModal ? (
                <OneTimeReminderFormContent
                  onSubmit={(formData) => createReminderMutation.mutate(formData)}
                  isLoading={createReminderMutation.isPending}
                  onClose={handleCloseReminderModal}
                  planId={reminderPlanContext.planId}
                  planTitle={reminderPlanContext.planTitle}
                />
              ) : showRecurringReminderModal ? (
                <RecurringReminderForm
                  onClose={handleCloseReminderModal}
                  onSubmit={handleRecurringReminderSubmit}
                  isLoading={createRecurringTemplateMutation.isPending}
                  error={createRecurringTemplateMutation.error instanceof Error ? createRecurringTemplateMutation.error.message : null}
                />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper component for one-time reminder form content
function OneTimeReminderFormContent({
  onSubmit,
  isLoading,
  onClose,
  planId,
  planTitle,
}: {
  onSubmit: (data: ReminderFormData) => void;
  isLoading: boolean;
  onClose: () => void;
  planId: string;
  planTitle: string;
}) {
  const [formState, setFormState] = useState<{
    title: string;
    description: string;
    date: string;
    time: string;
  }>({
    title: `Follow up: ${planTitle}`,
    description: '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
  });

  const handleSubmit = () => {
    if (!formState.title.trim() || !formState.date || !formState.time) {
      return;
    }
    onSubmit({
      title: formState.title,
      description: formState.description,
      date: formState.date,
      time: formState.time,
      planId,
    });
  };

  return (
    <>
      <div className="p-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#212121] mb-2">
            Title
          </label>
          <input
            type="text"
            value={formState.title}
            onChange={(e) =>
              setFormState((prev) => ({ ...prev, title: e.target.value }))
            }
            placeholder="e.g., Weekly progress check"
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#212121] mb-2">
            Description
          </label>
          <textarea
            value={formState.description}
            onChange={(e) =>
              setFormState((prev) => ({ ...prev, description: e.target.value }))
            }
            rows={3}
            placeholder="Add context or agenda for this reminder"
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Date
            </label>
            <input
              type="date"
              value={formState.date}
              onChange={(e) =>
                setFormState((prev) => ({ ...prev, date: e.target.value }))
              }
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Time
            </label>
            <input
              type="time"
              value={formState.time}
              onChange={(e) =>
                setFormState((prev) => ({ ...prev, time: e.target.value }))
              }
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
            />
          </div>
        </div>
      </div>
      <div className="p-4 bg-gray-50 border-t border-gray-200 flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 bg-gray-100 text-gray-600 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isLoading}
          className={`flex-1 font-semibold py-3 rounded-xl ${
            isLoading
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
              : 'bg-[#5B8E87] text-white'
          }`}
        >
          {isLoading ? 'Saving...' : 'Save Reminder'}
        </button>
      </div>
    </>
  );
}
